/**
 * App Meta Utility — Migrated from scio-v2 `client-utils.service.ts`
 *
 * Provides `getAssetDef(asset)` which resolves an asset's AppMeta definition
 * from backend-provided metadata. Used to determine per-type capabilities
 * (action gating, thumbnail display, preview behavior).
 *
 * → see scio-v2: `optisigns-portal-common/libs/internal/src/lib/common-core/services/client-utils.service.ts`
 * → see backend: `optisigns-open-api/src/app/app-meta/app-meta.model.ts`
 */
import type { AssetLike, AppMeta } from '@scio3/shared-types';
import {
  FILE_TYPES,
  FILE_ASSET_STATUS,
  ASSET_TYPES,
  SUB_TYPES,
  WEB_TYPES,
  COMMON_TYPE,
  APP_TYPES,
} from '@scio3/shared-types';
import { useAuthStore } from '@/stores/auth-store';

const CDN_URL = import.meta.env.VITE_CDN_URL || '';

// Module-level cache
let _assetDefs: Record<string, AppMeta> | null = null;
let _lastAppMetasRef: AppMeta[] | undefined;

/**
 * Resolves an asset's "alias type" — the key used to look up its AppMeta definition.
 *
 * Handles special cases (scrollingStrip, YouTube variants, splitScreen, smallapp extend)
 * then falls back through: appType → webType → commonType → fileType → subType → type
 *
 * Migrated from scio-v2 `ClientUtilsService.getAliasType(asset)`
 */
export function getAliasType(asset: AssetLike): string {
  // Exception: scrollingStrip subType takes priority
  if (asset.subType === SUB_TYPES.SCROLLING_STRIP) return SUB_TYPES.SCROLLING_STRIP;

  // YouTube with specific youtubeType (e.g., youtubeLive, youtubePlaylist)
  if (asset.webType === WEB_TYPES.YOUTUBE && asset.youtubeType) {
    return asset.youtubeType;
  }

  // Split screen uses commonType
  if (asset.type === ASSET_TYPES.SPLIT_SCREEN && asset.commonType) return asset.commonType;

  // Smallapp extend uses commonType
  if (asset.serviceType === COMMON_TYPE.SMALLAPP_EXTEND && asset.commonType) {
    return asset.commonType;
  }

  // Standard fallback chain
  return (
    asset.appType ||
    asset.webType ||
    asset.commonType ||
    asset.fileType ||
    asset.subType ||
    asset.type ||
    ''
  );
}

/**
 * Builds the asset definition lookup map from backend appMetas + hardcoded file-type entries.
 * Matches scio-v2 `ClientUtilsService.buildAssetDefs()`.
 */
function buildAssetDefs(appMetas: AppMeta[]): Record<string, AppMeta> {
  const defs: Record<string, AppMeta> = {};

  // Build from backend appMetas
  for (const meta of appMetas) {
    if (!meta.appType) continue;

    const def: AppMeta = { ...meta };

    // Resolve CDN image URLs for relative paths
    if (def.customClass && def.customClass.includes('.')) {
      def.customClass = resolveImageUrl(def.customClass);
    }
    if (def.image) {
      def.image = resolveImageUrl(def.image);
    }
    if (def.customIcon) {
      def.customIcon = resolveImageUrl(def.customIcon);
    }

    // canMoveTo defaults to true
    if (def.canMoveTo === undefined) {
      def.canMoveTo = true;
    }

    defs[meta.appType] = def;
  }

  // Hardcoded file-type entries (same as scio-v2)
  defs[APP_TYPES.CANVA_EXPORT] = {
    image: resolveImageUrl('canva_logo.png'),
    customIcon: resolveImageUrl('canva_logo.png'),
    thumbnail: true,
    canPreview: false,
    canDownload: true,
    canClone: true,
    canExpire: true,
    canMoveTo: true,
    canAssignPlaylist: true,
    copyAssetId: true,
    templateType: 1,
  };

  defs[FILE_TYPES.DOCUMENT] = {
    thumbnail: true,
    canPreview: false,
    canDownload: true,
    canClone: true,
    canExpire: true,
    canMoveTo: true,
    canAssignPlaylist: true,
    copyAssetId: true,
    templateType: 1,
  };

  defs[FILE_TYPES.IMAGE] = {
    thumbnail: true,
    canPreview: false,
    canDownload: true,
    canClone: true,
    canExpire: true,
    canMoveTo: true,
    canAssignPlaylist: true,
    copyAssetId: true,
    templateType: 1,
  };

  defs[FILE_TYPES.VIDEO] = {
    thumbnail: true,
    canPreview: false,
    canDownload: true,
    canClone: true,
    canExpire: true,
    canMoveTo: true,
    canAssignPlaylist: true,
    copyAssetId: true,
    templateType: 1,
  };

  defs[FILE_TYPES.AUDIO] = {
    thumbnail: false,
    canPreview: false,
    canDownload: true,
    canClone: true,
    canExpire: false,
    canMoveTo: true,
    canAssignPlaylist: false,
    copyAssetId: true,
    templateType: 1,
  };

  return defs;
}

/**
 * Resolves a relative image path to a full CDN URL.
 * Returns the original value if it's already a full URL or CDN is not configured.
 */
function resolveImageUrl(path: string): string {
  if (!path) return path;
  if (path.startsWith('http')) return path;
  if (!CDN_URL) return path;
  return `${CDN_URL}/scio/images/${path}`;
}

/**
 * Gets the AppMeta image URL for display (fallback icon/logo for app types without thumbnails).
 */
export function getAppMetaImageUrl(appMeta: AppMeta): string | null {
  return appMeta.image || appMeta.customIcon || null;
}

/**
 * Returns the AppMeta definition for an asset.
 *
 * Resolves the asset's alias type, looks up its definition from the backend-provided
 * appMetas data, and applies runtime status overrides:
 * - WAITING status: disables canPreview, canDownload, canExpire, canAssignPlaylist
 * - Non-ready status (CONVERTING etc): additionally disables canClone
 *
 * Migrated from scio-v2 `ClientUtilsService.getAssetDef(asset)`
 */
export function getAssetDef(asset: AssetLike): AppMeta {
  const me = useAuthStore.getState().me;
  const appMetas = me?.appMetas;

  // Rebuild cache if appMetas reference changed
  if (!_assetDefs || appMetas !== _lastAppMetasRef) {
    _assetDefs = buildAssetDefs(appMetas ?? []);
    _lastAppMetasRef = appMetas;
  }

  const aliasType = getAliasType(asset);
  const def: AppMeta = { ...(_assetDefs[aliasType] || {}) };

  // Runtime status overrides (matches scio-v2 behavior)
  const isReady = isAssetReady(asset);
  const isWaiting = isAssetWaiting(asset);

  if (isWaiting) {
    def.canPreview = false;
    def.canDownload = false;
    def.canExpire = false;
    def.canAssignPlaylist = false;
  } else if (!isReady) {
    def.canPreview = false;
    def.canDownload = false;
    def.canClone = false;
    def.canExpire = false;
    def.canAssignPlaylist = false;
  }

  return def;
}

/** Clears the cached asset definitions (call on logout or when appMetas change). */
export function clearAssetDefCache(): void {
  _assetDefs = null;
  _lastAppMetasRef = undefined;
}

function isAssetReady(asset: AssetLike): boolean {
  return (
    !asset.status ||
    asset.status === FILE_ASSET_STATUS.FINISHED ||
    asset.status === FILE_ASSET_STATUS.ACTIVE
  );
}

function isAssetWaiting(asset: AssetLike): boolean {
  return asset.status === FILE_ASSET_STATUS.WAITING;
}

/**
 * Checks if an appMeta entry should open the native SmallAppEditDialog.
 * Single source of truth — prevents routing mismatch between Assets page,
 * app creation modal, and Designer bridge (see #1007).
 */
export function isSmallAppEditable(appMeta: AppMeta | undefined | null): boolean {
  return (
    (appMeta?.func?.name === 'openSmallappEditModal' && !!appMeta?.backendUrl) ||
    !!appMeta?.hasManifest
  );
}
